// SocialCommentsPlaybook.js — Interactive Storyboard Edition + NVIDIA Live Case
import React, { useState, useEffect } from "react";

/* =========================
   1. Design Tokens & CSS
   ========================= */
const CSS = `
  :root {
    --brand: #0056D2;       
    --brand-10: #E6F0FF;
    --navy: #0B1A3C;        
    --navy-dark: #081226;
    --white: #FFFFFF;
    --ink: #0B1A3C;
    --muted: #64748B;
    --line: #E2E8F0;
    
    --orange: #F97316;
    --green: #10B981;
    --green-bg: #ECFDF5;
    
    --nvidia: #76B900; /* NVIDIA Green */
    
    --radius-lg: 16px;
    --shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  }

  * { box-sizing: border-box; }
  .scp-wrap {
    font-family: "Source Sans Pro", sans-serif;
    color: var(--ink); background: #F8FAFC;
    min-height: 100vh; padding-bottom: 80px;
  }

  /* --- Executive Lens Modal --- */
  .lens-overlay {
    position: fixed; inset: 0; z-index: 9999;
    background: rgba(8, 18, 38, 0.85); backdrop-filter: blur(6px);
    display: flex; align-items: center; justify-content: center;
    padding: 20px; animation: fadeIn 0.2s ease-out;
  }
  .lens-modal {
    background: var(--navy-dark); color: white;
    width: 100%; max-width: 900px;
    border-radius: 20px; border: 1px solid rgba(255,255,255,0.1);
    box-shadow: var(--shadow-xl); overflow: hidden;
    animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  }
  .lens-header {
    padding: 32px 40px; border-bottom: 1px solid rgba(255,255,255,0.1);
    display: flex; justify-content: space-between; align-items: flex-start;
  }
  .lens-tag {
    display: inline-block; background: var(--brand); color: white;
    font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px;
    padding: 6px 12px; border-radius: 99px; margin-bottom: 16px;
  }
  .lens-title { font-size: 32px; font-weight: 700; margin: 0 0 8px 0; }
  .lens-sub { font-size: 16px; color: rgba(255,255,255,0.6); margin: 0; line-height: 1.5; }
  
  .lens-grid { display: grid; grid-template-columns: 1.1fr 0.9fr; }
  @media (max-width: 800px) { .lens-grid { grid-template-columns: 1fr; } }

  .lens-left { padding: 40px; border-right: 1px solid rgba(255,255,255,0.1); }
  .lens-right { padding: 40px; background: rgba(255,255,255,0.02); }

  .lens-h3 {
    font-size: 12px; font-weight: 800; text-transform: uppercase; letter-spacing: 1.2px;
    margin-bottom: 24px; display: flex; align-items: center; gap: 8px;
  }
  .orange-text { color: var(--orange); }
  .green-text { color: var(--green); }
  
  .lens-list { list-style: none; padding: 0; margin: 0; }
  .lens-list li {
    position: relative; padding-left: 24px; margin-bottom: 16px;
    font-size: 15px; line-height: 1.6; color: rgba(255,255,255,0.9);
  }
  .lens-list li::before {
    content: "•"; position: absolute; left: 0; top: 0px; font-size: 18px;
  }
  .lens-left li::before { color: var(--orange); }
  
  .success-card {
    background: var(--white); color: var(--navy);
    border-radius: 12px; padding: 24px;
  }
  .success-list { list-style: none; padding: 0; margin: 0; }
  .success-list li {
    display: flex; gap: 12px; margin-bottom: 16px; font-size: 15px; line-height: 1.5;
  }
  .check-icon {
    min-width: 20px; height: 20px; border-radius: 50%; border: 1.5px solid var(--green);
    display: grid; place-items: center; color: var(--green); font-size: 12px; font-weight: 900;
  }

  .lens-footer {
    padding: 20px 40px; border-top: 1px solid rgba(255,255,255,0.1);
    display: flex; justify-content: flex-end; background: rgba(0,0,0,0.2);
  }
  
  .close-btn {
    background: rgba(255,255,255,0.1); border: none; color: white;
    width: 36px; height: 36px; border-radius: 50%; cursor: pointer;
    display: grid; place-items: center; transition: background 0.2s;
  }
  .close-btn:hover { background: rgba(255,255,255,0.2); }

  .action-btn {
    background: white; color: var(--navy); border: none;
    padding: 10px 20px; border-radius: 8px; font-weight: 700; cursor: pointer;
  }

  /* --- Storyboard / Simulator Styles --- */
  .story-container {
    background: white; border: 1px solid var(--line); border-radius: 16px;
    overflow: hidden; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
    position: relative; min-height: 550px;
  }
  .story-header {
    background: #F8FAFC; border-bottom: 1px solid var(--line);
    padding: 12px 20px; display: flex; justify-content: space-between; align-items: center;
  }
  .story-step {
    font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: 1px;
    color: var(--brand); background: var(--brand-10); padding: 4px 10px; border-radius: 6px;
  }
  
  /* LinkedIn Post View */
  .li-post { padding: 24px; }
  .li-stats {
    margin-top: 24px; padding: 12px 0; border-top: 1px solid var(--line); border-bottom: 1px solid var(--line);
    display: flex; justify-content: space-between; font-size: 13px; color: var(--muted);
  }
  .li-likes-btn {
    display: flex; align-items: center; gap: 6px; cursor: pointer;
    padding: 4px 8px; margin: -4px -8px; border-radius: 6px; transition: background 0.1s;
  }
  .li-likes-btn:hover { background: #F1F5F9; color: var(--brand); }
  .li-likes-btn:hover strong { color: var(--brand); }
  
  /* Modal Overlay (The "Likes" List) */
  .li-modal-overlay {
    position: absolute; inset: 0; background: rgba(0,0,0,0.4);
    display: flex; align-items: center; justify-content: center;
    animation: fadeIn 0.2s;
  }
  .li-modal {
    width: 380px; background: white; border-radius: 12px; box-shadow: 0 20px 40px rgba(0,0,0,0.2);
    display: flex; flexDirection: column; height: 450px;
  }
  .li-modal-hdr {
    padding: 12px 16px; border-bottom: 1px solid var(--line); font-weight: 700; font-size: 16px;
    display: flex; justify-content: space-between;
  }
  .li-list { overflow-y: auto; flex: 1; padding: 0; margin: 0; list-style: none; }
  .li-item {
    padding: 12px 16px; display: flex; gap: 12px; border-bottom: 1px solid #F1F5F9;
    align-items: flex-start;
  }
  .li-item.target { background: #F0FDFA; } /* Highlight targets */
  .li-av { width: 40px; height: 40px; border-radius: 50%; background: #E2E8F0; flex-shrink: 0; overflow:hidden; }
  .li-av img { width:100%; height:100%; object-fit:cover; }
  .li-info h4 { margin: 0; font-size: 14px; font-weight: 600; }
  .li-info p { margin: 2px 0 0; font-size: 12px; color: var(--muted); line-height: 1.3; }
  
  .copy-floater {
    position: absolute; bottom: 20px; left: 50%; transform: translateX(-50%);
    background: var(--navy); color: white; padding: 10px 20px; border-radius: 99px;
    font-weight: 700; font-size: 14px; box-shadow: 0 10px 20px rgba(0,0,0,0.2);
    cursor: pointer; display: flex; align-items: center; gap: 8px;
    animation: slideUp 0.3s;
  }
  .copy-floater:hover { transform: translateX(-50%) scale(1.05); }

  /* Results View */
  .result-view { padding: 30px; text-align: center; }
  .result-table {
    width: 100%; text-align: left; border-collapse: collapse; margin-top: 20px;
    border: 1px solid var(--line); border-radius: 8px; overflow: hidden;
  }
  .result-table th { background: #F8FAFC; padding: 10px; font-size: 11px; text-transform: uppercase; color: var(--muted); }
  .result-table td { border-top: 1px solid var(--line); padding: 10px; font-size: 13px; }
  .verified-badge { 
    background: var(--green-bg); color: #047857; padding: 2px 6px; 
    border-radius: 4px; font-size: 10px; font-weight: 700; text-transform: uppercase; 
  }

  /* --- Layout Helpers --- */
  .scp-header { background: white; border-bottom: 1px solid var(--line); padding: 20px 0; margin-bottom: 40px; }
  .scp-container { max-width: 1100px; margin: 0 auto; padding: 0 24px; }
  .persona-btn {
    width: 100%; text-align: left; padding: 16px 20px; margin-bottom: 12px;
    background: white; border: 1px solid var(--line); border-radius: 12px;
    display: flex; justify-content: space-between; align-items: center;
    cursor: pointer; transition: all 0.2s; box-shadow: 0 2px 4px rgba(0,0,0,0.03);
  }
  .persona-btn:hover { border-color: var(--brand); transform: translateY(-1px); }
  .persona-btn.active { border-color: var(--brand); background: var(--brand-10); }
  .persona-label { font-weight: 700; color: var(--navy); font-size: 16px; }
  
  /* NEW: NVIDIA Special styling */
  .persona-btn.nvidia { border-color: #76B900; background: #F2F9E5; }
  .persona-btn.nvidia.active { background: #76B900; border-color: #76B900; }
  .persona-btn.nvidia.active .persona-label { color: white; }
  .persona-btn.nvidia .persona-label { color: #385900; }

  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
  @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
`;

/* =========================
   2. Data & Content
   ========================= */
const PERSONA_DATA = {
  "l-and-d": {
    label: "L&D / Talent Leader",
    role: "CLO / VP Talent Dev",
    desc: "Impact, adoption, and proving the ROI of learning spend.",
    color: "#7C3AED",
    nightmare: ["Skills obsolescence outpacing training", "Low engagement with generic libraries", "Inability to prove ROI to CFO"],
    success: ["Verified skill acquisition (GenAI)", "Internal mobility > 40%", "Paths mapped to business roles"],
    questions: ["“Do you have a sandbox for engineers?”", "“Can we customize the skill path?”"],
    outreach: "Hi [Name]—saw you engaged with the skills gap post. You flagged the GenAI gap. We’re helping teams close it with role-based paths + practice labs. Happy to share a 2-min walkthrough.",
    
    // Simulator Content
    post_author: "Alex ThoughtLeader",
    post_role: "Chief Future Officer",
    post_text: "The skills gap isn’t coming. It's here. 🚨 Organizations are freezing because they don't know whether to buy, build, or borrow talent.",
    likes_count: 428,
    prospects: [
      { name: "Sarah Jenkins", role: "VP, Learning & Dev @ FinTech Co", relevant: true },
      { name: "David Chen", role: "Dir. Talent Management @ HealthSys", relevant: true },
      { name: "Mike Ross", role: "Account Executive @ SalesCo", relevant: false },
      { name: "Elena Rodriguez", role: "Chief Learning Officer @ Global Bank", relevant: true }
    ]
  },
  "tech-data": {
    label: "Tech & Data Leader",
    role: "CTO / VP Engineering",
    desc: "Velocity, security, and standardization of the tech stack.",
    color: "#0056D2",
    nightmare: ["Tech debt from unskilled juniors", "Security risks from 'Shadow AI'", "Devs watching videos but not shipping"],
    success: ["Faster shipping velocity", "Secure GenAI adoption", "Reduced onboarding time"],
    questions: ["“Is the assessment hands-on coding?”", "“Does it integrate with our IDE?”"],
    outreach: "Hi [Name], agreed with your point on verified application. We pair short modules with a sandbox + assessments so leaders can see who can ship. Want the 90-sec demo?",
    
    // Simulator Content
    post_author: "Alex ThoughtLeader",
    post_role: "Chief Future Officer",
    post_text: "Content isn’t the problem—verified application is. I need to see code shipping with the new tools.",
    likes_count: 312,
    prospects: [
      { name: "Marcus Thorne", role: "CTO @ SaaS ScaleUp", relevant: true },
      { name: "Lisa Wong", role: "VP Engineering @ Logistics Co", relevant: true },
      { name: "Kevin Hart", role: "Product Manager @ TechCo", relevant: false },
      { name: "Ash Patel", role: "Head of Data Science @ RetailGiant", relevant: true }
    ]
  },
  "hr-people": {
    label: "HR / People Leader",
    role: "CHRO / VP People",
    desc: "Retention, employer brand, and equitable growth.",
    color: "#DB2777",
    nightmare: ["Regrettable attrition of top performers", "Poor Glassdoor reviews", "Disengaged middle management"],
    success: ["High eNPS and retention", "Internal hiring ratio > 50%", "“Best Place to Work” awards"],
    questions: ["“Does this help with retention?”", "“Is the content equitable?”"],
    outreach: "Hi [Name], your ‘growth as retention’ comment resonated. We’re seeing engagement lift when skill paths tie to role mobility. Can I send a 1-pager with outcomes?",
    
    // Simulator Content
    post_author: "Alex ThoughtLeader",
    post_role: "Chief Future Officer",
    post_text: "Retention is the risk. Growth signals engagement—we need career paths that show momentum.",
    likes_count: 189,
    prospects: [
      { name: "Jessica Bloom", role: "Chief People Officer @ Media Co", relevant: true },
      { name: "Robert Fox", role: "VP HR Strategy @ AutoMotive", relevant: true },
      { name: "Danielle Yee", role: "HR Generalist @ Local Firm", relevant: false }
    ]
  },
  // --- NEW NVIDIA REAL EXAMPLE ---
  "nvidia-live": {
    label: "Live Example: NVIDIA",
    role: "Connecting the Org Chart",
    desc: "See how a single executive interview opens doors to Engineering, Product, and L&D leaders.",
    color: "#76B900", // NVIDIA Green
    nightmare: [
      "Hardware leadership means nothing without software fluency.",
      "Inference demand outstripping training compute.",
      "Keeping 250+ engineers skilled on the latest CUDA updates."
    ],
    success: [
      "Programmable data centers as single computing units.",
      "Workforce ready for generative video & real-world AI.",
      "Retention of top silicon and software talent."
    ],
    questions: [
      "“How do we scale AI infrastructure efficiency?”",
      "“Are our L&D programs aligned with the new 'inference' reality?”"
    ],
    outreach: "Hi [Name], saw you liked Pat Grady's convo with Michael Kagan. Michael's point about 'inference dwarfing training' really highlights the need for specialized skills. We're helping similar engineering orgs bridge that specific gap. Open to a 2-min brief?",
    
    // Simulator Content (Pat Grady Post)
    post_author: "Pat Grady",
    post_role: "Sequoia Capital",
    post_text: "Will the compute required for inference dwarf what's been required for pre-training? NVIDIA CTO Michael Kagan explains why the future of AI depends on rethinking data centers as single, programmable computing units.",
    likes_count: 90,
    prospects: [
      { name: "Cindy H. Hoffmann", role: "Learning & Development @ Nvidia", relevant: true },
      { name: "Karthick Iyer", role: "VP, Engineering @ Nvidia", relevant: true },
      { name: "Deepu Talla", role: "VP & GM, Embedded & Edge @ Nvidia", relevant: true },
      { name: "Martin Sedighi", role: "Recruiter @ MeritFinder", relevant: false },
      { name: "Colette Kress", role: "EVP & CFO @ Nvidia", relevant: true },
      { name: "Sonya Huang", role: "Partner @ Sequoia", relevant: false }
    ]
  }
};

/* =========================
   3. Components
   ========================= */

// --- The Executive Lens Modal (Static Info) ---
const LensModal = ({ personaKey, onClose }) => {
  if (!personaKey) return null;
  const p = PERSONA_DATA[personaKey];
  // Dynamic color for headers
  const hColor = personaKey === 'nvidia-live' ? '#76B900' : 'var(--orange)';
  
  return (
    <div className="lens-overlay" onClick={onClose}>
      <div className="lens-modal" onClick={e => e.stopPropagation()}>
        <div className="lens-header">
          <div>
            <span className="lens-tag" style={{background: p.color}}>Executive Lens</span>
            <h2 className="lens-title">{p.label}</h2>
            <p className="lens-sub">{p.desc}</p>
          </div>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="lens-grid">
          <div className="lens-left">
            <div className="lens-h3" style={{color: hColor}}>
              <span style={{fontSize:16}}>⚠️</span> What Keeps Them Up At Night
            </div>
            <ul className="lens-list">{p.nightmare.map((item, i) => <li key={i}>{item}</li>)}</ul>
            <div className="lens-h3" style={{color:'rgba(255,255,255,0.5)', marginTop:40}}>
              <span style={{fontSize:16}}>❓</span> Critical Questions
            </div>
            <div style={{fontStyle:'italic', color:'white', opacity:0.9, lineHeight:1.6}}>
              {p.questions.map((q, i) => <div key={i} style={{marginBottom:8}}>{q}</div>)}
            </div>
          </div>
          <div className="lens-right">
            <div className="success-card">
              <div className="lens-h3 green-text" style={{marginBottom:16}}>
                <span style={{fontSize:16}}>✅</span> What Success Looks Like
              </div>
              <ul className="success-list">
                {p.success.map((item, i) => (
                  <li key={i}><div className="check-icon">✓</div><span>{item}</span></li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <div className="lens-footer">
          <button className="action-btn" onClick={onClose}>Got it, close lens →</button>
        </div>
      </div>
    </div>
  );
};

// --- The Interactive Storyboard (The Core Demo) ---
const Storyboard = ({ personaKey }) => {
  const p = PERSONA_DATA[personaKey];
  const [step, setStep] = useState(0); // 0: Feed, 1: Likes Modal, 2: Result

  // Reset when persona changes
  useEffect(() => { setStep(0); }, [personaKey]);

  return (
    <div className="story-container">
      {/* Header / Instructions */}
      <div className="story-header">
        <div style={{display:'flex', alignItems:'center', gap:10}}>
          <span className="story-step">Step {step + 1}</span>
          <span style={{fontSize:13, fontWeight:600, color:'var(--navy)'}}>
            {step === 0 && "Identify a high-value post and click the Likes/Reactions."}
            {step === 1 && "The Goldmine: Open the list and copy everyone at once."}
            {step === 2 && "The Payoff: Paste into the tool to get a clean list."}
          </span>
        </div>
        {step > 0 && <button onClick={() => setStep(0)} style={{fontSize:11, color:'var(--muted)', background:'none', border:'none', cursor:'pointer', textDecoration:'underline'}}>Restart Demo</button>}
      </div>

      {/* STEP 0: THE FEED POST */}
      {step === 0 && (
        <div className="li-post">
          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            <div style={{ width: 40, height: 40, background: personaKey === 'nvidia-live' ? '#E2E8F0' : '#0A66C2', borderRadius: '50%', color: 'white', display:'grid', placeItems:'center', fontWeight:700, overflow:'hidden' }}>
               {/* Just a placeholder avatar or initials */}
               {personaKey === 'nvidia-live' ? 'PG' : 'A'}
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{p.post_author} • 1st</div>
              <div style={{ fontSize: 12, color: '#64748B' }}>{p.post_role} • 2h • Edited</div>
            </div>
          </div>
          <div style={{ fontSize: 14, lineHeight: 1.5, marginBottom: 16 }}>
            {p.post_text}
          </div>
          {personaKey === 'nvidia-live' && (
             // Mock image placeholder for Pat Grady post
             <div style={{width:'100%', height:180, background:'#333', borderRadius:8, marginBottom:16, display:'grid', placeItems:'center', color:'#aaa', fontSize:12}}>
               [Video / Article Preview]
             </div>
          )}
          
          {/* THE CLICKABLE TARGET */}
          <div className="li-stats">
            <div 
              className="li-likes-btn" 
              onClick={() => setStep(1)}
              title="Click here to see who liked this!"
            >
              <span>👍 👏 💡</span>
              <strong>{p.likes_count} (Click to view)</strong>
            </div>
            <span>112 comments</span>
          </div>
          <div style={{borderTop:'1px solid var(--line)', paddingTop:12, display:'flex', justifyContent:'space-around', fontSize:13, fontWeight:600, color:'var(--muted)'}}>
            <span>Like</span><span>Comment</span><span>Repost</span><span>Send</span>
          </div>
        </div>
      )}

      {/* STEP 1: THE LIKES MODAL */}
      {step === 1 && (
        <div className="li-post" style={{filter:'blur(2px)', pointerEvents:'none'}}>
           <div style={{height:300}}></div> 
        </div>
      )}
      {step === 1 && (
        <div className="li-modal-overlay">
          <div className="li-modal">
            <div className="li-modal-hdr">
              <span>Reactions ({p.likes_count})</span>
              <span>✕</span>
            </div>
            <ul className="li-list">
              {p.prospects.map((person, i) => (
                <li key={i} className={`li-item ${person.relevant ? 'target' : ''}`}>
                  <div className="li-av"></div>
                  <div className="li-info">
                    <h4>{person.name}</h4>
                    <p>{person.role}</p>
                    {person.relevant && <div style={{fontSize:10, color:'var(--brand)', marginTop:4, fontWeight:700}}>🎯 TARGET PERSONA</div>}
                  </div>
                </li>
              ))}
            </ul>
            <div className="copy-floater" onClick={() => setStep(2)}>
              <span>📋 Simulate "Select All & Copy"</span>
            </div>
          </div>
        </div>
      )}

      {/* STEP 2: THE RESULTS */}
      {step === 2 && (
        <div className="result-view">
          <div style={{fontSize:40, marginBottom:16}}>✨</div>
          <h3 style={{margin:0, color:'var(--navy)'}}>Parsed Data Ready for Outreach</h3>
          <p style={{color:'var(--muted)', fontSize:14, margin:'8px 0 20px'}}>
            We extracted {p.prospects.filter(x=>x.relevant).length} relevant buyers from the noise.
          </p>
          
          <table className="result-table">
            <thead>
              <tr><th>Name</th><th>Role</th><th>Company</th><th>Status</th></tr>
            </thead>
            <tbody>
              {p.prospects.filter(x => x.relevant).map((person, i) => (
                <tr key={i}>
                  <td>{person.name}</td>
                  <td>{person.role.split('@')[0]}</td>
                  <td>{person.role.split('@')[1]}</td>
                  <td><span className="verified-badge">Matched</span></td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{marginTop:30, padding:20, background:'#F1F5F9', borderRadius:12, textAlign:'left'}}>
            <div style={{fontSize:11, fontWeight:800, textTransform:'uppercase', color:'var(--muted)', marginBottom:8}}>Next Step: Send This</div>
            <div style={{fontFamily:'monospace', fontSize:13, color:'var(--navy)'}}>{p.outreach}</div>
          </div>
          
          <div style={{marginTop:20}}>
             <a href="#/tools/social-comments" style={{textDecoration:'none', fontWeight:700, color:'var(--brand)'}}>Go to Tool →</a>
          </div>
        </div>
      )}
    </div>
  );
};

/* =========================
   4. Main Render
   ========================= */
export default function SocialCommentsPlaybook() {
  const [activePersona, setActivePersona] = useState("l-and-d");
  const [modalPersona, setModalPersona] = useState(null);

  return (
    <div className="scp-wrap">
      <style>{CSS}</style>

      <header className="scp-header">
        <div className="scp-container">
          <h1 style={{ fontSize: 28, fontWeight: 800, color: 'var(--navy)', margin: 0 }}>Social Playbook: The "Like" Mining Strategy</h1>
          <p style={{ margin: '8px 0 0 0', color: 'var(--muted)' }}>
            Don't just read comments. Mine the 400+ likes to build a high-intent list in seconds.
          </p>
        </div>
      </header>

      <main className="scp-container">
        <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 40, alignItems: 'start' }}>
          
          {/* Left: Persona Selection */}
          <div>
            <h3 style={{ fontSize: 11, textTransform: 'uppercase', color: '#94A3B8', letterSpacing: 1, marginBottom: 12 }}>1. Choose Your Target</h3>
            {Object.entries(PERSONA_DATA).map(([key, data]) => (
              <div 
                key={key}
                className={`persona-btn ${key === 'nvidia-live' ? 'nvidia' : ''} ${activePersona === key ? 'active' : ''}`}
                onClick={() => {
                  setActivePersona(key); 
                  setModalPersona(key); 
                }}
              >
                <span className="persona-label">{data.label}</span>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: data.color }}></div>
              </div>
            ))}
            
            <div style={{ marginTop: 24, padding: 16, background: '#EFF6FF', borderRadius: 12, border: '1px solid #DBEAFE' }}>
              <div style={{ fontSize: 13, color: '#1E3A8A', lineHeight: 1.5 }}>
                <b>Why this works:</b> People who "Like" a problem-aware post are acknowledging they have that problem. They are warmer than a cold lookup.
              </div>
            </div>
          </div>

          {/* Right: The Interactive Demo */}
          <div>
            <h3 style={{ fontSize: 11, textTransform: 'uppercase', color: '#94A3B8', letterSpacing: 1, marginBottom: 12 }}>2. The Workflow (Interactive Demo)</h3>
            <Storyboard personaKey={activePersona} />
          </div>

        </div>
      </main>

      {/* The Executive Lens Modal */}
      {modalPersona && (
        <LensModal 
          personaKey={modalPersona} 
          onClose={() => setModalPersona(null)} 
        />
      )}
    </div>
  );
}
